import { useEffect, useRef } from "react";
import { analytics } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

function getSessionId(): string {
  let id = sessionStorage.getItem("section_tracking_session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("section_tracking_session", id);
  }
  return id;
}

/**
 * Tracks which sections a user scrolls into view on a page.
 * Each section fires a GA4 `section_view` event exactly once per page load
 * and is persisted to the section_views table for drop-off analysis.
 */
export function useSectionTracking(pageName: string, sectionNames: string[]) {
  const seen = useRef(new Set<string>());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingElements = useRef<Set<HTMLElement>>(new Set());

  useEffect(() => {
    const sessionId = getSessionId();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const name = (entry.target as HTMLElement).dataset.sectionName;
          if (name && !seen.current.has(name)) {
            seen.current.add(name);
            analytics.sectionView(name, pageName);
            // Persist to DB (fire-and-forget)
            (supabase.from("section_views" as any) as any)
              .insert({ session_id: sessionId, page_name: pageName, section_name: name })
              .then(() => {});
          }
        });
      },
      { threshold: 0.3 }
    );

    observerRef.current = observer;

    // Observe any elements registered before the effect ran
    pendingElements.current.forEach((el) => observer.observe(el));
    pendingElements.current.clear();

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [pageName]);

  const sectionRef = (index: number) => (el: HTMLElement | null) => {
    if (!el) return;
    el.dataset.sectionName = sectionNames[index];
    if (observerRef.current) {
      observerRef.current.observe(el);
    } else {
      pendingElements.current.add(el);
    }
  };

  return sectionRef;
}
